import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faCircleCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { Modal, ModalBody } from 'reactstrap';

import { currencyCode } from 'Helpers/Geo/data';
import useViewport from 'Hooks/useViewport';
import { findValidVoucher } from 'Redux/ducks/vouchers';
import { addVoucher } from 'Redux/ducks/voucher';
import store from 'Redux/store';
import Terms from 'Components/Vouchers/Terms';

import 'Components/Vouchers/VisibleVoucher.scss';
import 'Components/Modals/AccountModal.scss';


function VisibleVoucher({ vouchers, appliedVoucher, product, pickAndMix }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const { isBelowMd } = useViewport();

  const productPrice = get(product, `currentPrice.${currencyCode}`, 0);

  const openVoucher = () => {
    setCollapsed(!collapsed);
    localStorage.setItem('visibleVoucher', 'open');
    setTimeout(() => {
      // We want to delay showing the content so that it doesn't stretch the box during the animation
      setShowContent(true);
    }, 250);
  };

  const closeVoucher = useCallback(() => {
    setCollapsed(!collapsed);
    setShowContent(false);
    localStorage.setItem('visibleVoucher', 'closed');
  }, [collapsed]);

  // Getting the right voucher
  // The component is added in 3 places, product pages, pick and mix pages and generally around the site.
  // If used on product page it finds valid vouchers, get ones that are always visible and checks if the product is above the min spend.
  // Otherwise it just gets any visible voucher

  let validVoucher = null;

  if (!isEmpty(product)) {
    const validProductVouchers = findValidVoucher(product, vouchers);
    if (validProductVouchers) {
      let validVisibleVouchers = [];
      validVisibleVouchers = validProductVouchers.filter(
        v => v.always_visible && productPrice >= v.min_spend[currencyCode],
      );
      if (!isEmpty(validVisibleVouchers)) {
        validVoucher = validVisibleVouchers[0];
      }
    }
  } else {
    let validVisibleVouchers = [];
    if (!pickAndMix) {
      validVisibleVouchers = vouchers.filter(v => v.always_visible);
    }
    if (pickAndMix) {
      validVisibleVouchers = vouchers.filter(v => v.always_visible && v.pick_and_mix);
    }
    if (!isEmpty(validVisibleVouchers)) {
      validVoucher = validVisibleVouchers[0];
    }
  }

  useEffect(() => {
    const visibleVoucherLS = localStorage.getItem('visibleVoucher');
    if (visibleVoucherLS === 'closed') {
      setCollapsed(true);
      setShowContent(false);
    }
  }, []);

  useEffect(() => {
    if (isBelowMd) {
      const visibleVoucherLS = localStorage.getItem('visibleVoucher');
      if (!visibleVoucherLS) {
        setCollapsed(true);
        setShowContent(false);
      }
    } else {
      setCollapsed(true);
      setShowContent(false);
    }
  }, [isBelowMd]);

  if (!validVoucher) return false;

  const voucherId = get(validVoucher, '_id', '');
  const voucherPercent = get(validVoucher, 'percent', 0);
  const voucherCode = get(validVoucher, 'code', '');
  const appliedVoucherId = get(appliedVoucher, 'voucher._id', '');
  const voucherCurrentlyApplied = voucherId === appliedVoucherId;

  return (
    <React.Fragment>
      <div className={classNames('VisibleVoucher', { 'VisibleVoucher--collapsed': collapsed })}>
        {collapsed &&
          <button className="VisibleVoucher__collapsedButton" onClick={() => { openVoucher(); }}>
            <FontAwesomeIcon icon={faTag} />
          </button>
        }
        {!collapsed && showContent &&
          <div className="VisibleVoucher__voucherContent">
            <div className="VisibleVoucher__saveExtra">
              <FormattedMessage id="VisibleVoucher.text.save-extra" defaultMessage="Save an extra" />
            </div>
            <div className="VisibleVoucher__percentageSaving">
              {voucherPercent}%
            </div>
            {!voucherCurrentlyApplied &&
              <div className="VisibleVoucher__applyButtonContainer">
                <button className="VisibleVoucher__applyButton" onClick={() => { store.dispatch(addVoucher(voucherCode, null, true)); }}>
                  <FormattedMessage id="VisibleVoucher.button.apply" defaultMessage="Apply Coupon" />
                </button>
              </div>
            }
            {voucherCurrentlyApplied &&
              <div className="VisibleVoucher__couponApplied">
                <FontAwesomeIcon icon={faCircleCheck} />
                Coupon applied
              </div>
            }
            <div className="VisibleVoucher__exclusions">
              <button onClick={() => { setModalOpen(!modalOpen); }}>
                <FormattedMessage id="VisibleVoucher.button.exclusions" defaultMessage="Exclusions apply" />
              </button>
            </div>
          </div>
        }

        <div className="VisibleVoucher__badgeContainer">
          {collapsed &&
            <button className="VisibleVoucher__badge" onClick={() => { openVoucher(); }}>
              1
            </button>
          }
          {!collapsed &&
            <button className="VisibleVoucher__badge" onClick={() => { closeVoucher(); }}>
              <FontAwesomeIcon icon={faXmark} title="Close" />
            </button>
          }
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} className="account-modal address-modal">
        <ModalBody>
          <div className="account-modal-title">
            <FormattedMessage id="VisibleVoucher.modal.title" defaultMessage="Coupon Terms and Conditions" />
          </div>
          <div className="account-modal-form full-width">
            <Terms voucher={validVoucher} />
            <div className="account-modal-form-buttons">
              <div className="form-button-container">
                <button className="btn btn-primary" onClick={() => setModalOpen(!modalOpen)}>
                  <FormattedMessage id="VisibleVoucher.modal.button.close" defaultMessage="Close" />
                </button>
              </div>
            </div>
          </div>
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={() => setModalOpen(!modalOpen)}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
}

VisibleVoucher.propTypes = {
  vouchers: PropTypes.arrayOf(PropTypes.shape({})),
  voucher: PropTypes.shape({}),
  product: PropTypes.shape({}),
  pickAndMix: PropTypes.bool,
};

VisibleVoucher.defaultProps = {
  vouchers: [],
  appliedVoucher: {},
  product: {},
  pickAndMix: false,
};

const mapStateToProps = ({
  vouchers: { all: vouchersAll },
  voucher: appliedVoucher,
}) => ({
  vouchers: vouchersAll,
  appliedVoucher,
});

export default connect(mapStateToProps)(VisibleVoucher);
